import { createContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { auth } from '@/lib/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  type?: 'jobseeker' | 'referrer' | 'admin' | 'unknown';
  verified?: boolean;
  verificationStatus?: string;
  emailVerified?: boolean;
  companyName?: string;
  positionTitle?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('🔐 AuthProvider: Setting up Firebase auth listener');
    
    // Failsafe timeout to ensure isLoading becomes false
    const loadingTimeoutId = setTimeout(() => {
      console.warn('⚠️ AuthProvider: Loading timeout reached (10s), forcing isLoading to false');
      setIsLoading(false);
    }, 10000); // 10 second timeout
    
    // Load user from localStorage on mount
    const loadUserFromStorage = () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('✅ AuthProvider: Loaded user from localStorage', userData.email);
          setUser(userData);
        }
      } catch (error) {
        console.error('❌ AuthProvider: Error loading user from localStorage:', error);
      }
    };

    loadUserFromStorage();

    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔐 AuthProvider: Firebase auth state changed', {
        hasUser: !!firebaseUser,
        email: firebaseUser?.email,
        timestamp: new Date().toISOString()
      });

      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Get fresh token
          const freshToken = await firebaseUser.getIdToken();
          setToken(freshToken);
          console.log('✅ AuthProvider: Got fresh Firebase token');

          // Load user data from localStorage (already populated by useTokenAuth)
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            console.log('✅ AuthProvider: Set user from storage after Firebase auth');
          }
        } catch (error) {
          console.error('❌ AuthProvider: Error getting token:', error);
        }
      } else {
        setUser(null);
        setToken(null);
        console.log('🔐 AuthProvider: User logged out, cleared state');
      }

      console.log('🏁 AuthProvider: Setting isLoading to false after auth state change');
      setIsLoading(false);
      clearTimeout(loadingTimeoutId); // Clear timeout since we got a response
    });

    return () => {
      console.log('🔐 AuthProvider: Cleaning up auth listener');
      clearTimeout(loadingTimeoutId);
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      console.log('🔐 AuthProvider: Logging out');
      await auth.signOut();
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);
      setFirebaseUser(null);
      console.log('✅ AuthProvider: Logout successful');
    } catch (error) {
      console.error('❌ AuthProvider: Logout error:', error);
      throw error;
    }
  };

  const getToken = async (): Promise<string | null> => {
    try {
      if (firebaseUser) {
        // Use cached token instead of forcing refresh (false instead of true)
        const freshToken = await firebaseUser.getIdToken(false);
        setToken(freshToken);
        return freshToken;
      }
      return token;
    } catch (error) {
      console.error('❌ AuthProvider: Error getting token:', error);
      return null;
    }
  };

  const value: AuthContextType = useMemo(
    () => ({
      user,
      firebaseUser,
      token,
      isLoading,
      isAuthenticated: !!user && !!firebaseUser,
      setUser,
      setToken,
      logout,
      getToken,
    }),
    [user, firebaseUser, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
